import { isChargingStatistics, chargingParameter, chargingProduct } from './charging-statistics-model'
import { readPageProject } from '@/schema/page-project'
import { navigationIssue } from '@/runtime/page-navigation'
import { validateListData, usesPlatformData } from '@/components/FleetList/model'
import { isDeviceValue, deviceValueQuery } from '@/runtime/device-value-data'
import { isDeviceChart, deviceChartQuery } from '@/runtime/device-chart-data'
import { controlDeviceId } from '@/components/DeviceProperty/model'
import { locationParameter } from './device-map-model'
import { pageNavigationIssue, defaultPageNavigation } from '@/components/Navigation/pages'
import { deviceStatusQuery } from './device-status-model'
import { pageTextSources, pageTextSettings } from './page-context-text-model'
import { weeklyPlan } from '@/components/WeekSchedule/model'
import { normalizeControlValue } from '@/components/DeviceProperty/model'
import { deviceStatusDemoStatistics } from '@/components/DeviceStatus/presentation'

const object = value => value && typeof value === 'object' && !Array.isArray(value)
const present = value => value !== undefined && value !== null && String(value).trim() !== ''

// 只读配置：不求值脚本、不请求资源、不连接设备，也不改写旧单页数据。
export function checkPublicationConfig (record) {
  const issues = []
  const result = { errors: [], warnings: [], pages: 0, components: 0 }
  let model
  try { model = readPageProject(record) } catch (error) {
    result.errors.push({ severity: 'error', code: 'project-invalid', message: error.message || '大屏配置无法读取', location: '项目配置' })
    return result
  }
  const project = model.pageProject
  const persistentPages = !!model.metadata.pageProject
  result.pages = project.pages.length
  project.pages.forEach(page => {
    const add = (severity, code, message, node, path, tab = '1') => {
      const componentName = node && (node.name || node.title || (node.component || {}).prop || '未命名组件')
      issues.push({ severity, code, message, pageId: persistentPages ? page.id : '', pageName: page.name,
        componentId: node && node.index, componentPath: path || [], componentName, tab,
        location: page.name + (node ? ' / ' + componentName : ' / 页面配置') })
    }
    const walk = (nodes, parent = []) => nodes.forEach((node, index) => {
      const path = parent.concat(index)
      if (node.navigation) {
        const issue = node.navigation.type === 'product' && (!node.component || !['productList', 'deviceList'].includes(node.component.prop)) ? '按产品跳转仅适用于产品列表或设备列表组件'
          : node.navigation.type === 'pages' && (!node.component || node.component.prop !== 'navigation' || node.component.name !== 'navigation' || !node.option || node.option.mode !== 'pages') ? '多页面导航仅适用于页面导航组件' : navigationIssue(node.navigation, project.pages)
        if (issue) add('error', 'navigation-invalid', issue, node, path, '2')
      }
      if (node.children) { walk(node.children, path); return }
      result.components++
      const prop = node.component.prop, option = node.option || {}
      if (prop === 'navigation' && option.mode === 'pages') {
        const issue = pageNavigationIssue(node.navigation || defaultPageNavigation(), project.pages)
        if (issue && !issues.some(item => item.componentId === node.index && item.pageId === (persistentPages ? page.id : '') && item.message === issue)) add('error', 'page-navigation-config', issue, node, path, '0')
        if (Number(node.dataType || 0) !== 0 || node.url || node.dataScript || node.dataFormatter) add('error', 'page-navigation-data', '页面导航只读取本项目页面，不使用自定义接口、脚本或设备数据。', node, path, '0')
        return
      }
      if (prop === 'deviceStatus') {
        if (option.dataSource === 'static') {
          try { deviceStatusDemoStatistics(option.demoStatistics || node.data) } catch (error) { add('error', 'device-status-demo', error.message, node, path, '0') }
          return
        }
        try {
          if (node.component.name !== prop) throw new Error('设备统计组件类型不一致。')
          deviceStatusQuery(option, option.productScope === 'page' ? { productId: 'publication-context' } : {}, false)
          if (Number(node.dataType || 0) !== 0 || node.url || node.dataScript || node.dataFormatter || node.parameter) throw new Error('设备统计使用内建汇总接口，不使用自定义接口、脚本或设备属性数据。')
        } catch (error) { add('error', 'device-status-binding', error.message, node, path, '0') }
        if (option.productScope === 'page') add('warning', 'device-status-context', '设备统计需要上级页面传入 productId；正式运行不会使用编辑预览产品。', node, path, '0')
        add('warning', 'device-status-runtime', '设备总数和在线率按当前账号或发布授权范围汇总；未激活与未知状态不算离线，实际数据权限需在运行时验证。', node, path, '0')
        return
      }
      if (prop === 'deviceGroup') {
        try {
          if (node.component.name !== prop || !['groupId', 'productId'].every(key => typeof option[key] === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(option[key])) ||
              typeof option.propertyId !== 'string' || !/^[\p{L}\p{N}_.:-]{1,128}$/u.test(option.propertyId)) throw new Error('请选择设备群组和控制属性。')
          if (option.valueSource !== undefined && !['fixed', 'runtime'].includes(option.valueSource)) throw new Error('群控输入方式无效，请重新选择属性并保存。')
          if (option.valueSource !== 'runtime' || (option.targetValue !== undefined && option.targetValue !== null)) {
            if (!['string', 'boolean'].includes(typeof option.targetValue) && !(typeof option.targetValue === 'number' && Number.isFinite(option.targetValue))) throw new Error('请设置一个有效的固定群控目标值，或在属性面板改为运行时输入。')
            if (typeof option.targetValue === 'string' && option.targetValue.length > 4096) throw new Error('群控目标值过长。')
          }
          if (Number(node.dataType || 0) !== 0 || node.url || node.dataScript || node.dataFormatter || (node.navigation && node.navigation.type !== 'none')) throw new Error('群组控制不得携带自定义接口、脚本或页面跳转。')
        } catch (error) { add('error', 'group-control-binding', error.message, node, path, '0') }
        add('warning', 'group-control-runtime', '普通项目预览与正式运行可真实群控；分享须明确授权且授权用户为群组创建者。服务端逐台检查权限与物模型，最多100台，本批任务成员固定，不自动重发未知结果。', node, path, '0')
        return
      }
      if (prop === 'deviceProperty') {
        if (option.dataSource === 'static') {
          try { normalizeControlValue(option.demoProperty, option.demoValue) } catch (error) { add('error', 'property-demo', error.message, node, path, '0') }
          return
        }
        try {
          if (node.component.name !== prop || !present(option.propertyId) || String(option.propertyId).length > 128) throw new Error('请选择控制属性。')
          controlDeviceId(option, option.deviceSource === 'page' ? { deviceId: 'publication-context' } : {})
          if (option.productId && !/^[A-Za-z0-9_-]{1,64}$/.test(option.productId)) throw new Error('控制设备的产品限制编号无效。')
          if (Number(node.dataType || 0) !== 0 || node.url || node.dataScript || node.dataFormatter || (node.navigation && node.navigation.type !== 'none')) throw new Error('属性设置不得携带自定义接口、脚本或页面跳转。')
        } catch (error) { add('error', 'control-binding', error.message, node, path, '0') }
        add('warning', 'control-runtime', '普通项目预览及正式运行可真实下发；分享须明确授权“查看并控制”。执行时仍校验物模型可写、账号权限及设备资产范围；编辑画布和模板预览不下发。', node, path, '0')
        if (option.deviceSource === 'page') add('warning', 'control-context', '控制目标仅来自页面 deviceId，请通过设备列表进入；不会使用编辑预览设备。', node, path, '0')
        return
      }
      if (prop === 'weekSchedule') {
        try {
          if (Number(node.dataType || 0) === 2) {
            const parameter = node.parameter || {}, context = parameter.deviceSource === 'page' ? { deviceId: 'publication-context' } : {}
            deviceValueQuery(parameter, context)
            add('warning', 'readonly-device-context', '此组件按所选设备读取已上报数据；当前页面设备来源需要从设备列表进入。', node, path, '0')
          } else if (Number(node.dataType || 0) !== 0) throw new Error('请选择静态或设备数据来源。')
          else weeklyPlan(node.data)
        } catch (error) { add('error', 'readonly-device-config', error.message, node, path, '0') }
        return
      }
      if (['productList', 'deviceList'].includes(prop)) {
        const kind = prop === 'productList' ? 'product' : 'device'
        if (!usesPlatformData(option)) {
          const issue = validateListData(kind, node.data)
          if (issue) add('error', 'list-static-invalid', issue, node, path)
        } else add('warning', 'platform-list', '登录运行按访问者权限读取平台列表；新版分享按发布指定用户及所选角色／组织范围读取。产品、设备是否存在及实际访问权限尚未验证。', node, path)
        if (kind === 'product' && option.productScope === 'specified' && (!Array.isArray(option.productIds) || !option.productIds.length)) add('error', 'list-product-missing', '已选择指定产品范围，请至少选择一个产品。', node, path)
        if (kind === 'device' && option.productScope === 'current' && (usesPlatformData(option) || !present(option.demoProductId))) add('warning', 'product-context', '设备列表需要上级页面传入 productId，请通过产品列表进入。编辑示例产品不用于正式运行。', node, path)
        else if (kind === 'device' && !['all', 'current'].includes(option.productScope) && !present(option.productId)) add('error', 'list-product-missing', '设备列表缺少指定产品。', node, path)
        return
      }
      if (prop === 'text' && pageTextSources.includes(option.textSource)) {
        if (option.contextDataSource === 'static') return
        const settings = pageTextSettings(option)
        add('warning', settings.device ? 'text-device-context' : 'text-product-context', settings.label + '文本需要上级页面传入 ' + settings.contextKey + '；正式运行不使用编辑示例，名称按授权读取。', node, path)
        return
      }
      const type = Number(node.dataType === undefined ? 0 : node.dataType)
      if (type === 6) {
        try {
          if (!isChargingStatistics(node)) throw new Error('充电统计仅用于普通文本、折线图、柱状图或环形图。')
          const parameter = chargingParameter(node.parameter, prop)
          if (parameter.statisticsSource !== 'demo') {
            chargingProduct(parameter, parameter.productSource === 'page' ? { productId: 'publication-context' } : {})
            add('warning', 'charging-statistics-runtime', '充电统计按授权范围查询，启用后开始累计；约每10秒更新，不用演示值替代错误。请部署后端后验证。', node, path)
          }
        } catch (error) { add('error', 'charging-statistics-binding', error.message, node, path) }
      } else if (type === 5) {
        if (!['map', 'Dmap'].includes(prop)) add('error', 'map-device-config', '设备位置数据仅用于地图组件。', node, path)
        else {
          try { locationParameter(node.parameter || {}) } catch (error) { add('error', 'map-device-config', error.message, node, path) }
          if (node.parameter && node.parameter.scope === 'current') add('warning', 'map-product-context', '地图需要上级页面传入 productId；预览和正式运行不会使用编辑预览产品，缺少参数时不查询全部设备。', node, path)
          add('warning', 'map-runtime', '地图按当前账号或发布授权范围读取平台最新上报位置；ES数据、坐标系与实时权限需在运行时验证。', node, path)
        }
      } else if (type === 2) {
        const parameter = node.parameter === undefined ? {} : node.parameter
        if (!object(parameter)) { add('error', 'device-config', '设备数据参数必须为对象。', node, path); return }
        if (isDeviceChart(node) || isDeviceValue(node)) {
          const fromPage = parameter.deviceSource === 'page'
          if (fromPage) add('warning', 'device-context', '设备数据需要上级页面传入 deviceId，请通过设备列表进入。编辑示例设备不用于正式运行。', node, path)
          // 页面来源无需绑定示例设备，但属性、区间、聚合等仍须完整校验。
          const context = fromPage ? { deviceId: 'publication-context' } : {}
          try { (isDeviceChart(node) ? deviceChartQuery : deviceValueQuery)(parameter, context, false) } catch (error) { add('error', 'device-config', error.message, node, path) }
          add('warning', 'device-runtime', '设备及物模型属性是否存在、查询和订阅权限尚未验证，请在登录运行入口验收。', node, path)
        } else add('warning', 'legacy-device', '此组件沿用原设备数据处理，请在登录运行入口检查设备绑定和数据结果。', node, path)
      } else if (type === 1) {
        if (!present(node.url)) add('error', 'dynamic-url-missing', '动态数据缺少接口地址。', node, path)
        else add('warning', 'dynamic-runtime', '动态接口地址已配置；连通性、鉴权与响应格式需在运行时验证。', node, path)
      } else if (type === 3) {
        if (!present(node.dataScript)) add('error', 'script-missing', '脚本数据未填写脚本。', node, path)
        else add('warning', 'script-runtime', '脚本数据尚未执行，请在登录运行入口检查执行结果。', node, path)
      } else if (type === 4) add('warning', 'websocket-runtime', 'WebSocket 参数、主题与订阅权限需在运行时验证；本次检查不建立订阅。', node, path)
      if (node.dataFormatter) add('warning', 'formatter-runtime', '数据处理函数尚未执行，请在登录运行入口验证返回值。', node, path)
    })
    walk(page.nodes)
    if (!page.nodes.length) add('warning', 'empty-page', '当前页面没有组件，请确认是否保留空白页面。')
  })
  result.errors = issues.filter(issue => issue.severity === 'error')
  result.warnings = issues.filter(issue => issue.severity === 'warning')
  return result
}
